import { PythonTopicExercise } from '../../../../types/python-topic';

export const trabajandoConDatosExercises: PythonTopicExercise[] = [
  {
    id: 1,
    title: 'Procesar lista de números',
    description: 'Aprende a procesar una lista de números: calcular suma, promedio, máximo y mínimo.',
    initialCode: `# Procesa una lista de números
numeros = [10, 20, 30, 40, 50]

# Calcula la suma
suma = 

# Calcula el promedio
promedio = 

# Encuentra el máximo
maximo = 

# Encuentra el mínimo
minimo = 

print(f"Suma: {suma}")
print(f"Promedio: {promedio}")
print(f"Máximo: {maximo}")
print(f"Mínimo: {minimo}")`,
    solution: `# Procesa una lista de números
numeros = [10, 20, 30, 40, 50]

# Calcula la suma
suma = sum(numeros)

# Calcula el promedio
promedio = sum(numeros) / len(numeros)

# Encuentra el máximo
maximo = max(numeros)

# Encuentra el mínimo
minimo = min(numeros)

print(f"Suma: {suma}")
print(f"Promedio: {promedio}")
print(f"Máximo: {maximo}")
print(f"Mínimo: {minimo}")`,
    testCases: [],
    hints: [
      'Usa sum() para sumar todos los elementos',
      'El promedio es suma / cantidad',
      'Usa max() para encontrar el máximo',
      'Usa min() para encontrar el mínimo'
    ],
    validateOnlyCode: true,
    why: {
      title: '¿Por qué procesar datos?',
      content: `**Procesar datos** es una tarea fundamental en programación. Te permite extraer información útil de colecciones de datos.

### ¿Por qué este ejercicio?
- **Análisis de datos**: Calcular estadísticas básicas
- **Aplicación práctica**: Ventas, calificaciones, mediciones
- **Operaciones comunes**: Suma, promedio, máximo, mínimo son muy usadas
- **Fundamentos**: Base para análisis más complejos

### En el mundo real:
Cuando un sistema calcula estadísticas de ventas, procesa una lista de montos para obtener total, promedio, venta máxima y mínima. Sin procesamiento de datos, tendrías que calcular manualmente cada estadística.`,
      keyPoints: [
        'sum() suma todos los elementos de una lista',
        'El promedio es suma dividida por cantidad',
        'max() y min() encuentran valores extremos',
        'Estas operaciones son fundamentales para análisis de datos'
      ],
      realWorldExample: 'En un sistema de calificaciones, procesas una lista de notas para calcular promedio, nota más alta y más baja.'
    },
    theory: `# Procesar Listas de Números

## Operaciones Comunes

Python ofrece funciones built-in para procesar listas:

### Suma
\`\`\`python
numeros = [10, 20, 30]
suma = sum(numeros)  # 60
\`\`\`

### Promedio
\`\`\`python
numeros = [10, 20, 30]
promedio = sum(numeros) / len(numeros)  # 20.0
\`\`\`

### Máximo y Mínimo
\`\`\`python
numeros = [10, 20, 30]
maximo = max(numeros)  # 30
minimo = min(numeros)  # 10
\`\`\`

## Procesamiento Completo

Puedes combinar estas operaciones:

\`\`\`python
numeros = [10, 20, 30, 40, 50]
suma = sum(numeros)
promedio = suma / len(numeros)
maximo = max(numeros)
minimo = min(numeros)
\`\`\`

## Ventajas

- **Funciones built-in**: No necesitas escribir bucles manualmente
- **Eficiencia**: Optimizadas para rendimiento
- **Legibilidad**: Código más claro y conciso`,
    tips: [
      'Usa sum() para sumar todos los elementos',
      'El promedio es suma / cantidad de elementos',
      'max() y min() son funciones built-in muy útiles',
      'Puedes combinar estas operaciones para análisis completo'
    ],
    advice: [
      'Aprende las funciones built-in de Python',
      'Estas operaciones son la base del análisis de datos',
      'Practica procesando diferentes tipos de listas',
      'Considera casos edge como listas vacías'
    ]
  },
  {
    id: 2,
    title: 'Contar elementos',
    description: 'Aprende a contar elementos en una lista según diferentes criterios.',
    initialCode: `# Lista de números
numeros = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10]

# Cuenta números pares
pares = 

# Cuenta números mayores que 5
mayores_que_5 = 

print(f"Números pares: {pares}")
print(f"Números mayores que 5: {mayores_que_5}")`,
    solution: `# Lista de números
numeros = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10]

# Cuenta números pares
pares = sum(1 for n in numeros if n % 2 == 0)

# Cuenta números mayores que 5
mayores_que_5 = sum(1 for n in numeros if n > 5)

print(f"Números pares: {pares}")
print(f"Números mayores que 5: {mayores_que_5}")`,
    testCases: [],
    hints: [
      'Puedes usar un bucle for para contar',
      'Un número es par si n % 2 == 0',
      'Puedes usar sum() con una expresión generadora',
      'Incrementa un contador cuando se cumple la condición'
    ],
    validateOnlyCode: true,
    why: {
      title: '¿Por qué contar elementos?',
      content: `**Contar elementos** según criterios es fundamental para análisis de datos. Te permite entender patrones y características de tus datos.

### ¿Por qué este ejercicio?
- **Análisis de datos**: Contar elementos que cumplen condiciones
- **Aplicación práctica**: Filtros, estadísticas, reportes
- **Fundamentos**: Base para análisis más complejos
- **Eficiencia**: Formas eficientes de contar

### En el mundo real:
Cuando un sistema analiza ventas, cuenta cuántas ventas fueron mayores a cierto monto, o cuántas fueron en un rango de fechas. Sin contar elementos, no podrías generar estos reportes.`,
      keyPoints: [
        'Puedes contar con bucles for y contadores',
        'Usa condiciones para filtrar elementos',
        'sum() con expresiones generadoras es eficiente',
        'Contar es fundamental para análisis de datos'
      ],
      realWorldExample: 'En un sistema de inventario, cuentas cuántos productos tienen stock bajo, o cuántos están en oferta.'
    },
    theory: `# Contar Elementos

## Contar con Bucles

Puedes contar elementos con un bucle:

\`\`\`python
numeros = [1, 2, 3, 4, 5]
pares = 0
for n in numeros:
    if n % 2 == 0:
        pares += 1
\`\`\`

## Contar con sum()

Una forma más concisa usando \`sum()\`:

\`\`\`python
numeros = [1, 2, 3, 4, 5]
pares = sum(1 for n in numeros if n % 2 == 0)
\`\`\`

## Múltiples Condiciones

Puedes contar con diferentes criterios:

\`\`\`python
numeros = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10]
pares = sum(1 for n in numeros if n % 2 == 0)
mayores_que_5 = sum(1 for n in numeros if n > 5)
\`\`\`

## Ventajas

- **Flexibilidad**: Puedes contar con cualquier condición
- **Eficiencia**: Formas optimizadas de contar
- **Legibilidad**: Código claro y expresivo`,
    tips: [
      'Usa bucles for con contadores para contar',
      'sum() con expresiones generadoras es más conciso',
      'Define claramente la condición que quieres contar',
      'Puedes contar múltiples criterios en el mismo bucle'
    ],
    advice: [
      'Practica contando con diferentes condiciones',
      'Aprende a usar expresiones generadoras',
      'Considera casos edge como listas vacías',
      'Estas técnicas son fundamentales para análisis de datos'
    ]
  },
  {
    id: 3,
    title: 'Trabajar con secuencias y rangos',
    description: 'Aprende a usar range() para crear secuencias de números y trabajar con ellas.',
    initialCode: `# Crea un rango del 0 al 9
numeros = 

# Crea un rango del 5 al 15
rango_medio = 

# Crea un rango del 0 al 20 de 2 en 2
pares = 

print(f"Números 0-9: {list(numeros)}")
print(f"Rango 5-15: {list(rango_medio)}")
print(f"Pares 0-20: {list(pares)}")`,
    solution: `# Crea un rango del 0 al 9
numeros = range(10)

# Crea un rango del 5 al 15
rango_medio = range(5, 16)

# Crea un rango del 0 al 20 de 2 en 2
pares = range(0, 21, 2)

print(f"Números 0-9: {list(numeros)}")
print(f"Rango 5-15: {list(rango_medio)}")
print(f"Pares 0-20: {list(pares)}")`,
    testCases: [],
    hints: [
      'range(10) crea números del 0 al 9',
      'range(5, 16) crea del 5 al 15',
      'range(0, 21, 2) crea de 2 en 2',
      'range() es eficiente en memoria'
    ],
    validateOnlyCode: true,
    why: {
      title: '¿Por qué usar range()?',
      content: `**range()** permite crear secuencias de números de forma eficiente, sin almacenar todos los valores en memoria, ideal para bucles y secuencias grandes.

### ¿Por qué es esencial?
- **Eficiencia**: No almacena todos los valores en memoria
- **Bucles**: Ideal para iterar sobre números
- **Secuencias**: Genera números según patrones
- **Aplicación práctica**: Índices, iteraciones, generación de datos

### En el mundo real:
Cuando procesas datos por lotes de 100, usas range(0, len(datos), 100) para generar índices. Sin range(), tendrías que crear listas manualmente que consumen mucha memoria.`,
      keyPoints: [
        'range(10) crea números del 0 al 9',
        'range(inicio, fin) crea del inicio al fin-1',
        'range(inicio, fin, paso) crea con paso',
        'Eficiente en memoria'
      ],
      realWorldExample: 'En un sistema de procesamiento, range(0, len(archivos), 10) procesa archivos en lotes de 10.'
    },
    theory: `# Trabajar con Secuencias y Rangos

## ¿Qué es range()?

**range()** genera una secuencia de números sin almacenarlos todos en memoria.

## Sintaxis

\`\`\`python
range(10)           # 0 al 9
range(5, 16)        # 5 al 15
range(0, 21, 2)     # 0, 2, 4, 6, ..., 20
\`\`\`

## Características

### 1. range(fin)
Crea del 0 al fin-1:
\`\`\`python
range(10)  # 0, 1, 2, ..., 9
\`\`\`

### 2. range(inicio, fin)
Crea del inicio al fin-1:
\`\`\`python
range(5, 16)  # 5, 6, 7, ..., 15
\`\`\`

### 3. range(inicio, fin, paso)
Crea con paso específico:
\`\`\`python
range(0, 21, 2)  # 0, 2, 4, 6, ..., 20
\`\`\`

## Ventajas

- **Eficiencia**: No almacena todos los valores
- **Bucles**: Ideal para iterar
- **Flexibilidad**: Diferentes patrones`,
    tips: [
      'range(10) crea del 0 al 9',
      'range(5, 16) crea del 5 al 15',
      'range(0, 21, 2) crea de 2 en 2',
      'Eficiente en memoria',
      'Útil para bucles for'
    ],
    advice: [
      'Practica creando diferentes rangos',
      'Experimenta con diferentes pasos',
      'Aprende a usar range() en bucles',
      'Practica generando secuencias'
    ]
  },
  {
    id: 4,
    title: 'Filtrar listas con condiciones',
    description: 'Aprende a filtrar elementos de una lista según condiciones usando bucles y comprensiones.',
    initialCode: `# Lista de números
numeros = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10]

# Filtra números mayores que 5
mayores_que_5 = 

# Filtra números pares
pares = 

print(f"Mayores que 5: {mayores_que_5}")
print(f"Pares: {pares}")`,
    solution: `# Lista de números
numeros = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10]

# Filtra números mayores que 5
mayores_que_5 = [n for n in numeros if n > 5]

# Filtra números pares
pares = [n for n in numeros if n % 2 == 0]

print(f"Mayores que 5: {mayores_que_5}")
print(f"Pares: {pares}")`,
    testCases: [],
    hints: [
      'Usa comprensión de listas con if',
      '[n for n in numeros if condición]',
      'Solo elementos que cumplen la condición',
      'Más conciso que bucles for tradicionales'
    ],
    validateOnlyCode: true,
    why: {
      title: '¿Por qué filtrar listas?',
      content: `**Filtrar listas** permite extraer elementos que cumplen ciertas condiciones, esencial para procesamiento y análisis de datos.

### ¿Por qué es fundamental?
- **Selección**: Extraer elementos relevantes
- **Análisis**: Enfocarse en datos específicos
- **Aplicación práctica**: Filtros de búsqueda, reportes, análisis
- **Eficiencia**: Procesar solo datos relevantes

### En el mundo real:
Cuando un sistema de ventas filtra productos en oferta, crea una lista solo con productos que tienen descuento. Sin filtrado, tendrías que revisar todos los productos manualmente.`,
      keyPoints: [
        'Filtrar selecciona elementos que cumplen condiciones',
        'Comprensión de listas es concisa',
        'Útil para análisis de datos',
        'Puedes combinar múltiples condiciones'
      ],
      realWorldExample: 'En un sistema de usuarios, filtras usuarios activos: [u for u in usuarios if u.activo].'
    },
    theory: `# Filtrar Listas con Condiciones

## Filtrar con Comprensión de Listas

Puedes filtrar elementos usando comprensión de listas:
\`\`\`python
numeros = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10]
mayores_que_5 = [n for n in numeros if n > 5]
\`\`\`

## Múltiples Condiciones

Puedes combinar condiciones:
\`\`\`python
numeros = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10]
pares_mayores_que_5 = [n for n in numeros if n > 5 and n % 2 == 0]
\`\`\`

## Ventajas

- **Concisión**: Código más corto
- **Legibilidad**: Expresivo y claro
- **Eficiencia**: Procesa solo elementos relevantes`,
    tips: [
      'Usa comprensión de listas con if',
      '[n for n in lista if condición]',
      'Solo elementos que cumplen la condición',
      'Puedes combinar múltiples condiciones',
      'Más conciso que bucles for'
    ],
    advice: [
      'Practica filtrando con diferentes condiciones',
      'Experimenta con múltiples condiciones',
      'Aprende a usar comprensión de listas',
      'Practica en contextos de análisis de datos'
    ]
  },
  {
    id: 5,
    title: 'Transformar listas',
    description: 'Aprende a transformar elementos de una lista aplicando operaciones a cada elemento.',
    initialCode: `# Lista de números
numeros = [1, 2, 3, 4, 5]

# Crea una lista con el doble de cada número
dobles = 

# Crea una lista con el cuadrado de cada número
cuadrados = 

print(f"Dobles: {dobles}")
print(f"Cuadrados: {cuadrados}")`,
    solution: `# Lista de números
numeros = [1, 2, 3, 4, 5]

# Crea una lista con el doble de cada número
dobles = [n * 2 for n in numeros]

# Crea una lista con el cuadrado de cada número
cuadrados = [n ** 2 for n in numeros]

print(f"Dobles: {dobles}")
print(f"Cuadrados: {cuadrados}")`,
    testCases: [],
    hints: [
      'Usa comprensión de listas para transformar',
      '[n * 2 for n in numeros] multiplica cada elemento',
      '[n ** 2 for n in numeros] eleva al cuadrado',
      'Aplica operaciones a cada elemento'
    ],
    validateOnlyCode: true,
    why: {
      title: '¿Por qué transformar listas?',
      content: `**Transformar listas** permite aplicar operaciones a cada elemento, creando nuevas listas con valores modificados, esencial para procesamiento de datos.

### ¿Por qué es esencial?
- **Procesamiento**: Aplicar operaciones a colecciones
- **Análisis**: Calcular valores derivados
- **Aplicación práctica**: Precios con descuento, conversiones, cálculos
- **Eficiencia**: Procesar múltiples elementos de una vez

### En el mundo real:
Cuando un sistema calcula precios con IVA, transforma una lista de precios: [precio * 1.21 for precio in precios]. Sin transformación, tendrías que hacerlo elemento por elemento.`,
      keyPoints: [
        'Transformar aplica operaciones a cada elemento',
        'Comprensión de listas es concisa',
        'Crea nuevas listas con valores modificados',
        'Útil para cálculos en colecciones'
      ],
      realWorldExample: 'En un sistema de conversión, transformas temperaturas de Celsius a Fahrenheit: [c * 9/5 + 32 for c in celsius].'
    },
    theory: `# Transformar Listas

## Transformar con Comprensión de Listas

Puedes transformar elementos aplicando operaciones:
\`\`\`python
numeros = [1, 2, 3, 4, 5]
dobles = [n * 2 for n in numeros]  # [2, 4, 6, 8, 10]
\`\`\`

## Múltiples Transformaciones

Puedes aplicar diferentes operaciones:
\`\`\`python
numeros = [1, 2, 3, 4, 5]
cuadrados = [n ** 2 for n in numeros]  # [1, 4, 9, 16, 25]
\`\`\`

## Ventajas

- **Concisión**: Código más corto
- **Legibilidad**: Expresivo y claro
- **Eficiencia**: Procesa todos los elementos`,
    tips: [
      'Usa comprensión de listas para transformar',
      '[operacion(n) for n in lista]',
      'Aplica operaciones a cada elemento',
      'Crea nuevas listas con valores modificados',
      'Más conciso que bucles for'
    ],
    advice: [
      'Practica transformando con diferentes operaciones',
      'Experimenta con operaciones matemáticas',
      'Aprende a usar comprensión de listas',
      'Practica en contextos de procesamiento de datos'
    ]
  },
  {
    id: 6,
    title: 'Agrupar datos con diccionarios',
    description: 'Aprende a agrupar elementos de una lista usando diccionarios como contenedores.',
    initialCode: `# Lista de palabras
palabras = ["gato", "perro", "gato", "pájaro", "perro", "gato"]

# Agrupa palabras y cuenta cuántas veces aparece cada una
conteo = {}

for palabra in palabras:
    # Completa: incrementa el conteo de cada palabra
    # Si la palabra no existe, inicialízala en 0
    # Usa: conteo[palabra] = conteo.get(palabra, 0) + 1
    pass

print(f"Conteo: {conteo}")`,
    solution: `# Lista de palabras
palabras = ["gato", "perro", "gato", "pájaro", "perro", "gato"]

# Agrupa palabras y cuenta cuántas veces aparece cada una
conteo = {}

for palabra in palabras:
    # Completa: incrementa el conteo de cada palabra
    # Si la palabra no existe, inicialízala en 0
    # Usa: conteo[palabra] = conteo.get(palabra, 0) + 1
    conteo[palabra] = conteo.get(palabra, 0) + 1

print(f"Conteo: {conteo}")`,
    testCases: [],
    hints: [
      'Usa diccionarios para agrupar',
      'get() permite valor por defecto',
      'Incrementa el conteo en cada iteración',
      'Las claves son los elementos únicos'
    ],
    validateOnlyCode: true,
    why: {
      title: '¿Por qué agrupar datos?',
      content: `**Agrupar datos** permite organizar información por categorías, contar ocurrencias y analizar patrones, esencial para análisis estadístico y procesamiento de datos.

### ¿Por qué es poderoso?
- **Análisis**: Contar y agrupar elementos
- **Estadísticas**: Frecuencias, distribuciones
- **Aplicación práctica**: Análisis de texto, ventas por categoría, usuarios por región
- **Organización**: Estructurar datos de forma útil

### En el mundo real:
Cuando un sistema analiza ventas, agrupa productos por categoría para ver qué categoría vende más. Sin agrupación, tendrías que procesar cada venta individualmente.`,
      keyPoints: [
        'Diccionarios agrupan elementos por clave',
        'get() permite valores por defecto',
        'Incrementa conteos en cada iteración',
        'Útil para análisis estadístico'
      ],
      realWorldExample: 'En un sistema de logs, agrupas errores por tipo para ver qué errores son más comunes.'
    },
    theory: `# Agrupar Datos con Diccionarios

## Agrupar con Diccionarios

Puedes usar diccionarios para agrupar y contar:
\`\`\`python
palabras = ["gato", "perro", "gato"]
conteo = {}
for palabra in palabras:
    conteo[palabra] = conteo.get(palabra, 0) + 1
\`\`\`

## Cómo Funciona

1. **Inicialización**: Diccionario vacío
2. **Iteración**: Recorre cada elemento
3. **Conteo**: Incrementa el conteo de cada elemento
4. **Resultado**: Diccionario con conteos

## Ventajas

- **Organización**: Agrupa por categorías
- **Análisis**: Frecuencias y distribuciones
- **Eficiencia**: Procesa en una pasada`,
    tips: [
      'Usa diccionarios para agrupar',
      'get() permite valor por defecto',
      'Incrementa el conteo en cada iteración',
      'Las claves son elementos únicos',
      'Útil para análisis estadístico'
    ],
    advice: [
      'Practica agrupando diferentes tipos de datos',
      'Experimenta con diferentes criterios de agrupación',
      'Aprende a usar get() efectivamente',
      'Practica en contextos de análisis de datos'
    ]
  },
  {
    id: 7,
    title: 'Formatear strings con f-strings',
    description: 'Aprende a formatear strings usando f-strings para crear mensajes legibles.',
    initialCode: `# Variables
nombre = "Ana"
edad = 25
salario = 5000

# Formatea un mensaje con f-string
# Completa: "Hola, soy {nombre}, tengo {edad} años y gano $salario"
mensaje = 

print(mensaje)`,
    solution: `# Variables
nombre = "Ana"
edad = 25
salario = 5000

# Formatea un mensaje con f-string
# Completa: "Hola, soy {nombre}, tengo {edad} años y gano $salario"
mensaje = f"Hola, soy {nombre}, tengo {edad} años y gano $" + str(salario)

print(mensaje)`,
    testCases: [],
    hints: [
      'f"texto {variable}" formatea strings',
      'Las variables van entre llaves {}',
      'Puedes usar expresiones dentro de {}',
      'Más legible que concatenación'
    ],
    validateOnlyCode: true,
    why: {
      title: '¿Por qué usar f-strings?',
      content: `**f-strings** permiten formatear strings de forma legible y eficiente, insertando variables y expresiones directamente en el texto.

### ¿Por qué son mejores?
- **Legibilidad**: Código más claro que concatenación
- **Eficiencia**: Más rápidas que otros métodos
- **Expresiones**: Puedes usar expresiones dentro de {}
- **Aplicación práctica**: Mensajes, logs, reportes, interfaces

### En el mundo real:
Cuando un sistema genera reportes, usa f-strings para crear mensajes como "Total de ventas: $10,000 en 50 transacciones". Sin f-strings, tendrías que concatenar strings manualmente.`,
      keyPoints: [
        'f"texto {variable}" formatea strings',
        'Las variables van entre llaves {}',
        'Puedes usar expresiones dentro de {}',
        'Más legible que concatenación'
      ],
      realWorldExample: 'En un sistema de notificaciones, f-strings crean mensajes personalizados: f"Hola {usuario.nombre}, tienes {cantidad} mensajes nuevos".'
    },
    theory: `# Formatear Strings con f-strings

## ¿Qué son f-strings?

**f-strings** permiten insertar variables y expresiones directamente en strings.

## Sintaxis

\`\`\`python
nombre = "Ana"
edad = 25
mensaje = f"Hola, soy {nombre}, tengo {edad} años"
\`\`\`

## Características

### 1. Variables
Puedes insertar variables:
\`\`\`python
f"Hola {nombre}"
\`\`\`

### 2. Expresiones
Puedes usar expresiones:
\`\`\`python
f"Total: {precio * cantidad}"
\`\`\`

### 3. Formato
Puedes formatear números:
\`\`\`python
f"Precio: {precio:.2f}"  # 2 decimales
\`\`\`

## Ventajas

- **Legibilidad**: Código más claro
- **Eficiencia**: Más rápidas
- **Expresiones**: Puedes usar expresiones`,
    tips: [
      'f"texto {variable}" formatea strings',
      'Las variables van entre llaves {}',
      'Puedes usar expresiones dentro de {}',
      'Más legible que concatenación',
      'Prefiere f-strings sobre otros métodos'
    ],
    advice: [
      'Practica formateando diferentes tipos de datos',
      'Experimenta con expresiones en f-strings',
      'Aprende a formatear números',
      'Practica creando mensajes legibles'
    ]
  },
  {
    id: 8,
    title: 'Combinar filtrado y transformación',
    description: 'Aprende a combinar filtrado y transformación en una sola operación usando comprensión de listas.',
    initialCode: `# Lista de precios
precios = [100, 200, 300, 400, 500]

# Filtra precios mayores que 250 y aplica 10% de descuento
precios_con_descuento = 

print(f"Precios con descuento: {precios_con_descuento}")`,
    solution: `# Lista de precios
precios = [100, 200, 300, 400, 500]

# Filtra precios mayores que 250 y aplica 10% de descuento
precios_con_descuento = [precio * 0.9 for precio in precios if precio > 250]

print(f"Precios con descuento: {precios_con_descuento}")`,
    testCases: [],
    hints: [
      'Combina filtrado (if) y transformación en comprensión',
      '[operacion(x) for x in lista if condicion]',
      'Primero filtra, luego transforma',
      'Más eficiente que hacerlo en pasos separados'
    ],
    validateOnlyCode: true,
    why: {
      title: '¿Por qué combinar filtrado y transformación?',
      content: `**Combinar filtrado y transformación** en una sola operación es muy común y eficiente. Te permite procesar datos en un solo paso.

### ¿Por qué es eficiente?
- **Una pasada**: Procesa datos en una sola iteración
- **Concisión**: Código más corto y claro
- **Eficiencia**: Más rápido que pasos separados
- **Aplicación práctica**: Muy común en procesamiento de datos

### En el mundo real:
Cuando un sistema procesa productos y necesita precios con descuento solo de productos en oferta, usa: [precio * 0.9 for precio in precios if producto.en_oferta]. Sin comprensiones, necesitarías múltiples pasos.`,
      keyPoints: [
        'Combina filtrado (if) y transformación',
        '[operacion(x) for x in lista if condicion]',
        'Más eficiente que hacerlo en pasos separados',
        'Muy común en procesamiento de datos'
      ],
      realWorldExample: 'En un sistema de análisis, filtras y transformas datos: [dato * 2 for dato in datos if dato > 10].'
    },
    theory: `# Combinar Filtrado y Transformación

## Comprensión con Filtrado y Transformación

Puedes combinar ambas operaciones:
\`\`\`python
precios = [100, 200, 300, 400, 500]
precios_con_descuento = [precio * 0.9 for precio in precios if precio > 250]
\`\`\`

## Orden de Operaciones

1. **Filtrado**: if condición selecciona elementos
2. **Transformación**: operacion(x) modifica elementos seleccionados

## Ventajas

- **Eficiencia**: Una sola pasada
- **Concisión**: Código más corto
- **Legibilidad**: Expresivo y claro`,
    tips: [
      'Combina filtrado (if) y transformación',
      '[operacion(x) for x in lista if condicion]',
      'Primero filtra, luego transforma',
      'Más eficiente que pasos separados',
      'No abuses - a veces pasos separados son más legibles'
    ],
    advice: [
      'Practica combinando filtrado y transformación',
      'Experimenta con diferentes condiciones y operaciones',
      'Aprende cuándo combinar vs separar',
      'Practica en contextos de procesamiento de datos'
    ]
  },
  {
    id: 9,
    title: 'Trabajar con listas de diccionarios',
    description: 'Aprende a procesar listas que contienen diccionarios, extrayendo y transformando datos.',
    initialCode: `# Lista de productos (diccionarios)
productos = [
    {"nombre": "Laptop", "precio": 999, "stock": 5},
    {"nombre": "Mouse", "precio": 25, "stock": 10},
    {"nombre": "Teclado", "precio": 75, "stock": 8}
]

# Extrae solo los nombres de los productos
nombres = 

# Calcula el valor total del inventario (precio * stock para cada producto)
valor_total = 

print(f"Nombres: {nombres}")
print(f"Valor total: {valor_total}")`,
    solution: `# Lista de productos (diccionarios)
productos = [
    {"nombre": "Laptop", "precio": 999, "stock": 5},
    {"nombre": "Mouse", "precio": 25, "stock": 10},
    {"nombre": "Teclado", "precio": 75, "stock": 8}
]

# Extrae solo los nombres de los productos
nombres = [p["nombre"] for p in productos]

# Calcula el valor total del inventario (precio * stock para cada producto)
valor_total = sum(p["precio"] * p["stock"] for p in productos)

print(f"Nombres: {nombres}")
print(f"Valor total: {valor_total}")`,
    testCases: [],
    hints: [
      'Accede a valores de diccionarios con ["clave"]',
      'p["nombre"] obtiene el nombre del producto',
      'Puedes usar comprensión de listas con diccionarios',
      'sum() puede sumar expresiones generadoras'
    ],
    validateOnlyCode: true,
    why: {
      title: '¿Por qué trabajar con listas de diccionarios?',
      content: `**Listas de diccionarios** son estructuras muy comunes en programación, especialmente cuando trabajas con datos de APIs, bases de datos o archivos JSON.

### ¿Por qué es esencial?
- **Estructura común**: Formato natural para datos estructurados
- **APIs y JSON**: Formato estándar de intercambio de datos
- **Procesamiento**: Extraer y transformar datos de colecciones
- **Aplicación práctica**: Muy común en desarrollo real

### En el mundo real:
Cuando un sistema recibe datos de una API, típicamente recibe una lista de objetos JSON que se convierten en listas de diccionarios. Procesar estos datos es fundamental.`,
      keyPoints: [
        'Listas de diccionarios son muy comunes',
        'Accede a valores con ["clave"]',
        'Puedes usar comprensión de listas',
        'Útil para procesar datos de APIs y JSON'
      ],
      realWorldExample: 'En un sistema de e-commerce, procesas una lista de productos de una API: [p["precio"] for p in productos if p["en_oferta"]].'
    },
    theory: `# Trabajar con Listas de Diccionarios

## Estructura Común

Las listas de diccionarios son muy comunes:
\`\`\`python
productos = [
    {"nombre": "Laptop", "precio": 999},
    {"nombre": "Mouse", "precio": 25}
]
\`\`\`

## Extraer Datos

Puedes extraer valores específicos:
\`\`\`python
nombres = [p["nombre"] for p in productos]
\`\`\`

## Procesar Datos

Puedes calcular valores:
\`\`\`python
valor_total = sum(p["precio"] * p["stock"] for p in productos)
\`\`\`

## Ventajas

- **Estructura común**: Formato natural
- **Procesamiento**: Fácil extraer y transformar
- **APIs**: Formato estándar`,
    tips: [
      'Accede a valores con ["clave"]',
      'Puedes usar comprensión de listas',
      'Filtra con condiciones en diccionarios',
      'Transforma extrayendo valores específicos',
      'Útil para procesar datos de APIs'
    ],
    advice: [
      'Practica procesando listas de diccionarios',
      'Experimenta extrayendo diferentes valores',
      'Aprende a filtrar y transformar',
      'Practica con datos similares a APIs reales'
    ]
  },
  {
    id: 10,
    title: 'Ordenar y buscar en listas',
    description: 'Aprende a ordenar listas y buscar elementos usando métodos built-in de Python.',
    initialCode: `# Lista de números
numeros = [5, 2, 8, 1, 9, 3]

# Ordena la lista (crea una nueva lista ordenada)
ordenados = 

# Busca el índice del número 8
indice_8 = 

# Verifica si 7 está en la lista
tiene_7 = 

print(f"Ordenados: {ordenados}")
print(f"Índice de 8: {indice_8}")
print(f"¿Tiene 7?: {tiene_7}")`,
    solution: `# Lista de números
numeros = [5, 2, 8, 1, 9, 3]

# Ordena la lista (crea una nueva lista ordenada)
ordenados = sorted(numeros)

# Busca el índice del número 8
indice_8 = numeros.index(8)

# Verifica si 7 está en la lista
tiene_7 = 7 in numeros

print(f"Ordenados: {ordenados}")
print(f"Índice de 8: {indice_8}")
print(f"¿Tiene 7?: {tiene_7}")`,
    testCases: [],
    hints: [
      'sorted() crea una nueva lista ordenada',
      'index() encuentra el índice de un elemento',
      '"in" verifica si un elemento está en la lista',
      'sorted() no modifica la lista original'
    ],
    validateOnlyCode: true,
    why: {
      title: '¿Por qué ordenar y buscar?',
      content: `**Ordenar y buscar** son operaciones fundamentales para organizar y encontrar datos, esenciales para análisis, reportes y procesamiento eficiente.

### ¿Por qué son esenciales?
- **Organización**: Datos ordenados son más fáciles de analizar
- **Búsqueda**: Encontrar elementos rápidamente
- **Aplicación práctica**: Reportes, análisis, interfaces
- **Eficiencia**: Operaciones optimizadas

### En el mundo real:
Cuando un sistema genera un reporte de ventas, ordena los productos por monto y busca los top 10. Sin ordenar y buscar, tendrías que procesar manualmente todos los datos.`,
      keyPoints: [
        'sorted() ordena sin modificar la original',
        'index() encuentra el índice',
        '"in" verifica pertenencia',
        'Operaciones fundamentales para análisis'
      ],
      realWorldExample: 'En un sistema de rankings, ordenas usuarios por puntaje y buscas la posición de un usuario específico.'
    },
    theory: `# Ordenar y Buscar en Listas

## Ordenar

Puedes ordenar listas:
\`\`\`python
numeros = [5, 2, 8, 1, 9, 3]
ordenados = sorted(numeros)  # [1, 2, 3, 5, 8, 9]
\`\`\`

## Buscar Índice

Puedes encontrar el índice:
\`\`\`python
numeros = [5, 2, 8, 1, 9, 3]
indice = numeros.index(8)  # 2
\`\`\`

## Verificar Pertenencia

Puedes verificar si existe:
\`\`\`python
numeros = [5, 2, 8, 1, 9, 3]
tiene_7 = 7 in numeros  # False
\`\`\`

## Ventajas

- **Organización**: Datos ordenados
- **Búsqueda**: Encontrar elementos
- **Eficiencia**: Operaciones optimizadas`,
    tips: [
      'sorted() crea nueva lista ordenada',
      'index() encuentra el índice',
      '"in" verifica pertenencia',
      'sorted() no modifica la original',
      'Útil para análisis y reportes'
    ],
    advice: [
      'Practica ordenando diferentes tipos de datos',
      'Experimenta buscando elementos',
      'Aprende cuándo usar sorted() vs sort()',
      'Practica en contextos de análisis de datos'
    ]
  }
];
